import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  isIosDevice,
  isStandalonePwa,
  type BeforeInstallPromptEvent,
} from "@/lib/pwaInstall";

interface PwaInstallContextValue {
  /** Show the "Install app" entry at all. */
  canShowInstallItem: boolean;
  /** A native install prompt is available (Chromium). */
  canPromptInstall: boolean;
  /** Whether the iOS instructions sheet is open. */
  instructionsOpen: boolean;
  /** Fire the native prompt; no-op if unavailable. */
  promptInstall: () => Promise<void>;
  openInstallInstructions: () => void;
  closeInstallInstructions: () => void;
}

const PwaInstallContext = createContext<PwaInstallContextValue | null>(null);

export function PwaInstallProvider({ children }: { children: ReactNode }) {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(() => isStandalonePwa());
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  useEffect(() => {
    // The service worker (and therefore any install eligibility) only exists in
    // production builds, so skip the listeners entirely in dev.
    if (!import.meta.env.PROD) return;

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      setInstructionsOpen(false);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    // The event can only be used once; drop it so the item hides afterwards.
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const openInstallInstructions = useCallback(
    () => setInstructionsOpen(true),
    [],
  );
  const closeInstallInstructions = useCallback(
    () => setInstructionsOpen(false),
    [],
  );

  const value = useMemo<PwaInstallContextValue>(() => {
    const canPromptInstall = !!deferredPrompt && !installed;
    const canShowIosInstructions =
      isIosDevice() && !installed && !canPromptInstall;
    return {
      canShowInstallItem: canPromptInstall || canShowIosInstructions,
      canPromptInstall,
      instructionsOpen,
      promptInstall,
      openInstallInstructions,
      closeInstallInstructions,
    };
  }, [
    deferredPrompt,
    installed,
    instructionsOpen,
    promptInstall,
    openInstallInstructions,
    closeInstallInstructions,
  ]);

  return (
    <PwaInstallContext.Provider value={value}>
      {children}
    </PwaInstallContext.Provider>
  );
}

export function usePwaInstall() {
  const ctx = useContext(PwaInstallContext);
  if (!ctx) {
    throw new Error("usePwaInstall must be used within PwaInstallProvider");
  }
  return ctx;
}
